import type { useMutation } from '@apollo/client/react/compiled'
import type { ConfirmButtonTransitionState } from '@components/ConfirmButton'
import type { IFilter } from '@components/Filter'
import type { AutocompleteChoiceType } from '@components/FormAutocompleteTextField'
import type { Node } from '@dolpheen/apollo'

export interface DialogProps {
  open: boolean
  onClose: () => void
}

export interface ListSettings<TColumn extends string = string> {
  columns?: TColumn[]
  columnWidths?: Record<string, number>
  rowNumber: number
}

export enum ListViews {
  APP_LIST = 'APP_LIST',
  BUSINESS_ENTITY_LIST = 'BUSINESS_ENTITY_LIST',
  CASE_LIST_ALL = 'CASE_LIST_ALL',
  CASE_LIST_USER_ASSIGNED = 'CASE_LIST_USER_ASSIGNED',
  CASE_LIST_TEAM_ASSIGNED = 'CASE_LIST_TEAM_ASSIGNED',
  CASE_SETTINGS_LIST = 'CASE_SETTINGS_LIST',
  CUSTOMER_LIST = 'CUSTOMER_LIST',
  FINANCIAL_PROVIDER_LIST = 'FINANCIAL_PROVIDER_LIST',
  TICKET_LIST_USER_ASSIGNED = 'TICKET_LIST_USER_ASSIGNED',
  TICKET_LIST_TEAM_ASSIGNED = 'TICKET_LIST_TEAM_ASSIGNED',
  TICKET_LIST_ALL = 'TICKET_LIST_ALL',
  USER_LIST = 'USER_LIST',
  USER_TEAM_LIST = 'USER_TEAM_LIST',
}

export interface ListProps<TColumns extends string = string> {
  isDisabled: boolean
  settings?: ListSettings<TColumns>
  onUpdateListSettings?: (key: keyof ListSettings<TColumns>, value: any) => void
  onListSettingsReset?: () => void
}

export interface SortPage<TSortKey extends string> {
  sort: Sort<TSortKey>
  onSort: (field: TSortKey, id?: string) => void
}

export interface PageListProps<TColumns extends string = string> extends ListProps<TColumns> {
  defaultSettings?: ListSettings<TColumns>
}

export interface SearchProps {
  onSearchChange: (value: string) => void
}
export interface SearchPageProps extends SearchProps {
  initialSearch: string
}
export interface FilterPageProps<TKeys extends string, TOpts>
  extends FilterProps<TKeys>, SearchPageProps, TabPageProps {
  filterOpts: TOpts
}

export interface FilterProps<TKeys extends string> {
  onFilterChange: (filter: IFilter<TKeys>) => void
}

export interface TabPageProps {
  currentTab: number
  tabs: string[]
  onAll: () => void
  onTabChange: (tab: number) => void
  onTabDelete: () => void
  onTabSave: () => void
}

export interface PartialMutationProviderOutput<
  TData extends Record<string, any> = object,
  TVariables extends Record<string, any> = object,
> {
  opts: useMutation.Result<TData> & MutationResultAdditionalProps
  /** Resolves with the mutation result, for a caller that chains a second
   *  write on the first one's data; most callers ignore it. */
  mutate: (variables: TVariables) => ReturnType<useMutation.MutationFunction<TData, TVariables>>
}

export interface CustomOperationProviderOutput<TVariables extends Record<string, any> = object> {
  opts: Pick<useMutation.Result, 'loading' | 'error'> & MutationResultAdditionalProps
  mutate: (variables: TVariables) => Promise<void>
}

export type Pagination = Partial<{
  after: string
  before: string
}>

export type Dialog<TDialog extends string> = Partial<{
  action: TDialog
}>
export type ActiveTab<TTab extends string = string> = Partial<{
  activeTab: TTab
}>
export type Filters<TFilters extends string> = Partial<Record<TFilters, string>>
export type FiltersWithMultipleValues<TFilters extends string> = Partial<Record<TFilters, string[]>>
export type FiltersAsDictWithMultipleValues<TFilters extends string> = Partial<
  Record<TFilters, Record<string, string[]>>
>
export type Search = Partial<{
  query: string
}>
export type SingleAction = Partial<{
  id: string
}>
export type Sort<TSort extends string = string> = Partial<{
  asc: boolean
  sort: TSort
}>
export type BulkAction = Partial<{
  ids: string[]
}>

export interface FetchMoreProps {
  isLoading: boolean
  hasMore: boolean
  onFetchMore: () => void
  totalCount?: number
}

export type TabActionDialog = 'save-search' | 'delete-search'

export interface MutationResultAdditionalProps {
  status: ConfirmButtonTransitionState
  /** Upload progress from 0 to 1 (only present for operations with file uploads) */
  uploadProgress?: number
}

export type MinMax = Record<'min' | 'max', string>

export interface FilterOpts<T> {
  active: boolean
  value: T
}

export enum StatusType {
  INFO = 'info',
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success',
}

export interface LocalizedStatus {
  localized: string
  status: StatusType
}

export type RelayToFlat<T extends { edges: Array<{ node: Node }> }> = ReadonlyArray<
  T['edges'][number]['node']
>

export type RelayNode<T extends { edges: ReadonlyArray<{ readonly node: Node }> }> =
  T['edges'][number]['node']

export interface AutocompleteFilterOpts extends FetchMoreProps, SearchPageProps {
  choices: AutocompleteChoiceType[]
  displayValues: AutocompleteChoiceType[]
}
